import { Obj } from 'src/hooks/useSpdSearch';
import { getResponseValueByParam, parseDateInput, toNumber } from 'utilities';

/**
 * Form-field UID the expensive-car flow writes so the CMS FlowRestrictionBlocks
 * (`ModalContentComponent`, watching via RHF) can react. Kept prefix-free so it
 * doesn't collide with the car page's `calculatorParameterPrefix` filter in the
 * calculate payload.
 */
export const EXPENSIVE_CAR_UIDS = {
	CATEGORY: 'EXPENSIVE_CAR_CATEGORY',
} as const;

/**
 * Static-fields UIDs holding the numeric thresholds. Authored on the car
 * product's `Static Fields` tab in Optimizely so business can tune limits
 * without a code deploy.
 */
export const EXPENSIVE_CAR_STATIC_FIELD_UIDS = {
	OSS_THRESHOLD: 'EXPENSIVE_CAR_OSS_THRESHOLD',
	GPS_THRESHOLD: 'EXPENSIVE_CAR_GPS_THRESHOLD',
	OLD_YEARS: 'EXPENSIVE_CAR_OLD_YEARS',
} as const;

export type ExpensiveCarCategory = 'NORMAL' | 'GPS_REQUIRED' | 'GPS_REQUIRED_OLD' | 'MANUAL_HANDLING';

export type ExpensiveCarConfig = {
	ossThresholdDkk: number;
	gpsThresholdDkk: number;
	oldCarYears: number;
};

export type ExpensiveCarAgeInput =
	| { kind: 'registrationDate'; value: string | undefined | null }
	| { kind: 'modelYear'; value: string | number | undefined | null };

export type SaveFormValue = (uid: string, value: unknown) => void;

export const readExpensiveCarConfig = (staticFields: StaticFields | undefined): ExpensiveCarConfig | undefined => {
	const items = staticFields?.items;
	if (!items?.length) return undefined;
	const findValue = (uid: string): string | undefined =>
		items.find((i) => i?.content?.uniqueIdentifier?.uid === uid)?.content?.defaultValue;
	const oss = toNumber(findValue(EXPENSIVE_CAR_STATIC_FIELD_UIDS.OSS_THRESHOLD));
	const gps = toNumber(findValue(EXPENSIVE_CAR_STATIC_FIELD_UIDS.GPS_THRESHOLD));
	const years = toNumber(findValue(EXPENSIVE_CAR_STATIC_FIELD_UIDS.OLD_YEARS));
	if (oss === undefined || gps === undefined || years === undefined) return undefined;
	return { ossThresholdDkk: oss, gpsThresholdDkk: gps, oldCarYears: years };
};

export const isCarOlderThanYears = (ageInput: ExpensiveCarAgeInput, years: number, now: Date = new Date()): boolean => {
	const cutoff = new Date(now.getTime());
	cutoff.setFullYear(cutoff.getFullYear() - years);

	if (ageInput.kind === 'registrationDate') {
		const date = parseDateInput(ageInput.value);
		return date !== undefined && date.getTime() < cutoff.getTime();
	}

	const year = toNumber(ageInput.value);
	if (year === undefined) return false;
	return new Date(year, 0, 1).getTime() < cutoff.getTime();
};

/**
 * Categorise the vehicle by SPD `NEW_VALUE_REG` and vehicle age, driving which
 * of the three CMS `FlowRestrictionBlock`s (if any) fires on the CarSearch step.
 * Defaults to `NORMAL` — and warns — when the CMS config is missing.
 */
export const deriveExpensiveCarCategory = ({
	newValue,
	ageInput,
	config,
	now = new Date(),
}: {
	newValue: number | undefined;
	ageInput: ExpensiveCarAgeInput;
	config: ExpensiveCarConfig | undefined;
	now?: Date;
}): ExpensiveCarCategory => {
	if (!config) {
		console.warn('deriveExpensiveCarCategory: expensiveCarConfig missing on metaArea, defaulting to NORMAL');
		return 'NORMAL';
	}
	if (newValue === undefined) return 'NORMAL';
	if (newValue > config.ossThresholdDkk) return 'MANUAL_HANDLING';
	if (newValue > config.gpsThresholdDkk) {
		return isCarOlderThanYears(ageInput, config.oldCarYears, now) ? 'GPS_REQUIRED_OLD' : 'GPS_REQUIRED';
	}
	return 'NORMAL';
};

/**
 * Write the derived expensive-car category UID into RHF via the caller's
 * `saveFormValue` so `ModalContentComponent`'s `watch(uid)` inside the CMS
 * FlowRestrictionBlocks re-evaluates. Returns the derived category for callers
 * that want to log/branch.
 */
export const applyExpensiveCarFields = ({
	variantDetails,
	ageInput,
	config,
	saveFormValue,
	now = new Date(),
}: {
	variantDetails: Obj;
	ageInput: ExpensiveCarAgeInput;
	config: ExpensiveCarConfig | undefined;
	saveFormValue: SaveFormValue;
	now?: Date;
}): ExpensiveCarCategory => {
	const newValue = toNumber(getResponseValueByParam(variantDetails, 'NEW_VALUE_REG'));
	const category = deriveExpensiveCarCategory({ newValue, ageInput, config, now });
	saveFormValue(EXPENSIVE_CAR_UIDS.CATEGORY, category);
	return category;
};

/**
 * Wipe the expensive-car UID from RHF at the start of a new lookup so a stale
 * `MANUAL_HANDLING` can't keep the OSS flow-stop up after the user picks a
 * normal car.
 */
export const clearExpensiveCarFields = (saveFormValue: SaveFormValue): void => {
	saveFormValue(EXPENSIVE_CAR_UIDS.CATEGORY, undefined);
};
